// 時間前 札作成 (個別・一括) と下書きの編集の手数料自動考慮 (AC-BIN-004b / AC-BIN-104) の表示への写像。
// 入札価格の算出と値域・入札制限の判定は backend のプレビュー RPC が持つ (#9213)。 応答の解釈はスポットと
// 共通の `~/models/feeAutoConsiderPreview` が持ち、 ここは時間前の proto 型をその入力に写す。
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingIntradayFeeAdjustedPriceBound,
  BiddingIntradayFeeAdjustedPriceBoundSource,
  type BiddingIntradayFeeAdjustedPriceResult,
  BiddingIntradayFeeAdjustedPriceStatus,
  type BiddingIntradayFeeAutoConsiderState,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { PreviewBiddingIntradayOrderFeeAdjustedPricesResponse } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import type { TFunction } from 'i18next'

import {
  type FeeAdjustedPricePreviewQueryStatus,
  type FeeAdjustedPriceResult,
  type FeeAdjustmentViolation,
  type FeeAutoConsiderHint,
  type FeeAutoConsiderMatrixSetting,
  type FeeAutoConsiderPreview,
  type FeeAutoConsiderState,
  type FeeAutoConsiderTreatment,
  feeAdjustmentViolationLines,
  previewQueryStatus,
  resolveFeeAutoConsiderPreview as sharedResolveFeeAutoConsiderPreview,
  resolveFeeFallbackOffPreview as sharedResolveFeeFallbackOffPreview,
} from '~/models/feeAutoConsiderPreview'

export {
  type FeeAdjustedPricePreviewQueryStatus,
  type FeeAdjustmentViolation,
  type FeeAutoConsiderBlockedReason,
  type FeeAutoConsiderConfirmed,
  type FeeAutoConsiderHint,
  type FeeAutoConsiderMatrixSetting,
  type FeeAutoConsiderPreview,
  type FeeAutoConsiderTreatment,
  fallbackOffPreview,
  feeAutoConsiderHint,
  formatYenPerKwh,
  previewQueryStatus,
  revealsFeeUnavailable,
  toFeeAutoConsiderMatrixSetting,
  usesDesiredPrice,
} from '~/models/feeAutoConsiderPreview'

/** AC-BSP-106 の 2 行 (改行でつなぐ)。 */
export const feeAdjustmentViolationMessage = (
  t: TFunction,
  v: FeeAdjustmentViolation,
): string => {
  const { line1, line2 } = feeAdjustmentViolationLines(t, v)
  return `${line1}\n${line2}`
}

const STATUS: Record<
  BiddingIntradayFeeAdjustedPriceStatus,
  FeeAdjustedPriceResult['status']
> = {
  [BiddingIntradayFeeAdjustedPriceStatus.UNSPECIFIED]: undefined,
  [BiddingIntradayFeeAdjustedPriceStatus.OFF]: 'off',
  [BiddingIntradayFeeAdjustedPriceStatus.ADJUSTED]: 'adjusted',
  [BiddingIntradayFeeAdjustedPriceStatus.OUT_OF_RANGE]: 'outOfRange',
  [BiddingIntradayFeeAdjustedPriceStatus.INDETERMINATE]: 'indeterminate',
}

const BOUND: Record<
  BiddingIntradayFeeAdjustedPriceBound,
  FeeAdjustmentViolation['bound'] | undefined
> = {
  [BiddingIntradayFeeAdjustedPriceBound.UNSPECIFIED]: undefined,
  [BiddingIntradayFeeAdjustedPriceBound.MIN]: 'min',
  [BiddingIntradayFeeAdjustedPriceBound.MAX]: 'max',
}

const SOURCE: Record<
  BiddingIntradayFeeAdjustedPriceBoundSource,
  FeeAdjustmentViolation['source'] | undefined
> = {
  [BiddingIntradayFeeAdjustedPriceBoundSource.UNSPECIFIED]: undefined,
  [BiddingIntradayFeeAdjustedPriceBoundSource.JEPX]: 'jepx',
  [BiddingIntradayFeeAdjustedPriceBoundSource.BID_LIMIT]: 'bidLimit',
}

const toFeeState = (
  state: BiddingIntradayFeeAutoConsiderState,
): FeeAutoConsiderState => ({
  enabled: state.enabled,
  feeYenPerKwh: state.feeYenPerKwh?.value,
  unavailable: state.unavailable,
})

const toFeeResult = (
  result: BiddingIntradayFeeAdjustedPriceResult,
): FeeAdjustedPriceResult => ({
  status: STATUS[result.status],
  orderPriceYenPerKwh: result.orderPriceYenPerKwh?.value,
  violation:
    result.violation === undefined
      ? undefined
      : {
          bound: BOUND[result.violation.bound],
          source: SOURCE[result.violation.source],
          boundOrderPriceYenPerKwh:
            result.violation.boundOrderPriceYenPerKwh?.value,
          desiredPriceBoundYenPerKwh:
            result.violation.desiredPriceBoundYenPerKwh?.value,
        },
})

/** 時間前の応答を共通の判定 (`~/models/feeAutoConsiderPreview`) に渡す。 */
export const resolveFeeAutoConsiderPreview = ({
  status,
  state,
  result,
  side,
  treatment,
}: {
  status: FeeAdjustedPricePreviewQueryStatus
  state: BiddingIntradayFeeAutoConsiderState | undefined
  result: BiddingIntradayFeeAdjustedPriceResult | undefined
  side: Side
  treatment: FeeAutoConsiderTreatment
}): FeeAutoConsiderPreview =>
  sharedResolveFeeAutoConsiderPreview({
    status,
    state: state === undefined ? undefined : toFeeState(state),
    result: result === undefined ? undefined : toFeeResult(result),
    side,
    treatment,
  })

export const resolveFeeFallbackOffPreview = ({
  status,
  result,
  setting,
}: {
  status: FeeAdjustedPricePreviewQueryStatus
  result: BiddingIntradayFeeAdjustedPriceResult | undefined
  setting: FeeAutoConsiderMatrixSetting
}): FeeAutoConsiderPreview =>
  sharedResolveFeeFallbackOffPreview({
    status,
    result: result === undefined ? undefined : toFeeResult(result),
    setting,
  })

/**
 * 手数料自動考慮 (時間前) の状態の取得状況。 `pending` は最初の応答待ち、 `unavailable` は取得失敗・
 * 失敗した後の再試行中・ON で単価を使えない応答 (AC-BIN-004b の「手数料単価を取得できない間」)。
 */
export type IntradayFeeState =
  | { kind: 'pending' }
  | { kind: 'unavailable' }
  | { kind: 'off' }
  | { kind: 'fee'; feeYenPerKwh: string }

const classifyFeeAutoConsiderState = (
  state: BiddingIntradayFeeAutoConsiderState,
): Exclude<IntradayFeeState, { kind: 'pending' }> => {
  if (!state.enabled) return { kind: 'off' }
  const fee = state.feeYenPerKwh?.value
  return state.unavailable || fee === undefined
    ? { kind: 'unavailable' }
    : { kind: 'fee', feeYenPerKwh: fee }
}

/**
 * 入札編集モーダル (AC-BIN-007) が、 開いた時点で決める手数料自動考慮の扱い (AC-BIN-004b)。
 *
 * - `fee`: ON で手数料単価を使える。 価格欄は「希望価格」で、 初期値は保存済みの入札価格から
 *   backend が逆算した値 (`initialDesiredPriceYenPerKwh`)
 * - `off`: OFF の扱い。 価格欄は「入札価格」で、 初期値は保存済みの入札価格
 * - `unavailable`: 手数料単価を取得できず OFF として扱う。 価格欄は「入札価格」で、 入力値を
 *   そのまま入札価格にする
 * - `none`: 手数料の扱いが無い (成行 = AC-BIN-004d・表示専用)
 * - `pending`: 逆算の応答待ち。 入力欄は出さない
 */
export type IntradayFeeEditBaseline =
  | { kind: 'pending' }
  | { kind: 'none' }
  | { kind: 'off' }
  | { kind: 'unavailable' }
  | {
      kind: 'fee'
      feeYenPerKwh: string
      initialDesiredPriceYenPerKwh: string
    }

type FeeStateQuery = {
  data: PreviewBiddingIntradayOrderFeeAdjustedPricesResponse | undefined
  isPending: boolean
  isError: boolean
  failureCount: number
}

/** 手数料自動考慮の状態を取るプレビュー RPC の取得状況を、 待つ・取得できない・OFF・ON で単価を使える に分ける。 */
export const resolveIntradayFeeState = ({
  data,
  isPending,
  isError,
  failureCount,
}: FeeStateQuery): IntradayFeeState => {
  switch (
    previewQueryStatus({
      waiting: isPending,
      isError,
      failureCount,
      hasData: data !== undefined,
    })
  ) {
    case 'loading':
      return { kind: 'pending' }
    case 'error':
      return { kind: 'unavailable' }
    case 'success':
      return data?.feeAutoConsider === undefined
        ? { kind: 'unavailable' }
        : classifyFeeAutoConsiderState(data.feeAutoConsider)
  }
}

/**
 * 編集を開いた時に逆算を問い合わせた応答 (状態 + 保存済みの入札価格の逆算) から、 編集の扱いを決める。
 * 逆算は backend が持つ (符号の規則を画面で持ち直さない)。 逆算の無い応答も手数料単価を使えないとする。
 */
export const resolveIntradayFeeEditBaseline = (
  query: FeeStateQuery,
): IntradayFeeEditBaseline => {
  const fetched = resolveIntradayFeeState(query)
  if (fetched.kind !== 'fee') return fetched
  const desired =
    query.data?.desiredPriceResults[0]?.desiredPriceYenPerKwh?.value
  if (desired === undefined) return { kind: 'unavailable' }
  return {
    kind: 'fee',
    feeYenPerKwh: fetched.feeYenPerKwh,
    initialDesiredPriceYenPerKwh: desired,
  }
}

/**
 * 作成・編集を開いた時に、 閉じるまで OFF として扱うか (AC-BIN-004b)。 画面が開く前から取得している
 * 手数料自動考慮の状態が、 取得中・取得失敗・再試行中・単価を使えない のいずれかなら OFF として扱う。
 * 入札制限の設定で OFF と確定していれば手数料単価を見ない (#8052)。
 */
export const opensAsFeeFallbackOff = (
  state: IntradayFeeState,
  setting: FeeAutoConsiderMatrixSetting,
): boolean => {
  if (setting.status === 'success' && !setting.enabled) return false
  return state.kind === 'pending' || state.kind === 'unavailable'
}

/**
 * 入札編集モーダルの補足表示の文言 key。 作成と同じ表示のうち、 保存を止める理由の 2 つ
 * (取得できない・設定が変わった) だけは「作成できません」 でなく「保存できません」 にする。
 */
export const toEditFeeHintKey = (key: FeeAutoConsiderHint['key']): string =>
  key === 'feeAutoConsiderUnavailable' ||
  key === 'feeAutoConsiderSettingMismatch'
    ? `${key}Edit`
    : key
